/* eslint-disable react/jsx-props-no-spreading */
// eslint-disable-next-line no-redeclare, @typescript-eslint/no-redeclare
import { Button, Center, List, Text, ThemeIcon } from '@mantine/core';
import { FC, useEffect, useState } from 'react';
import { IconFileDescription } from '@tabler/icons';
import { closeAllModals, openModal } from '@mantine/modals';
import { i18n } from '@lingui/core';
import { BookitupEntity, EntityType, FileS } from '../../Service/FileS';
import SkeletonItems from '../../Atoms/SkeletonItems';
import EmptyText from '../../Atoms/EmptyText';
import FilePicker from '../../Atoms/FilePicker';

interface Props {
  eventId?: string;
  profileId: string;
  onSelect: (entity: BookitupEntity) => void;
  allowedExtensions?: string[];
  entityType?: EntityType;
  accept?: string;
}

export const openFilePickerModal = (props: Props) =>
  openModal({
    title: (
      <Text weight="bolder" size="xl">
        Datei auswählen
      </Text>
    ),
    children: <FilePickerModal {...props} />,
  });

const FilePickerModal: FC<Props> = ({
  eventId,
  profileId,
  onSelect,
  allowedExtensions = [],
  entityType = 'profiles',
  accept,
}) => (
  <>
    <Text weight="bolder">Dateien aus Deinem Profil</Text>
    <EntitiesList
      entityId={profileId}
      entityType={entityType}
      allowedExtensions={allowedExtensions}
      onSelect={onSelect}
      accept={accept}
    />
    {eventId && (
      <>
        <Text weight="bolder" mt="xl">
          Dateien aus diesem Event
        </Text>
        <EntitiesList
          entityId={eventId}
          entityType="events"
          allowedExtensions={allowedExtensions}
          onSelect={onSelect}
          accept={accept}
        />
      </>
    )}
    <Center mt="xl">
      <Button variant="subtle" onClick={() => closeAllModals()}>
        {i18n._('actions.close')}
      </Button>
    </Center>
  </>
);

const EntitiesList: FC<{
  entityId: string;
  entityType: EntityType;
  allowedExtensions: string[];
  onSelect: (entity: BookitupEntity) => void;
  accept?: string;
}> = ({ entityId, entityType, allowedExtensions = [], onSelect, accept }) => {
  const [loading, setLoading] = useState(false);
  const [files, setFiles] = useState<BookitupEntity[]>([]);

  // eslint-disable-next-line no-underscore-dangle
  const _onSelect = (entity: BookitupEntity) => {
    onSelect(entity);
    closeAllModals();
  };

  useEffect(() => {
    setLoading(true);
    FileS.getEntityFiles(entityType, entityId)
      .then((resJSON) => {
        const entities = resJSON
          .map((file) => ({
            entityId,
            entityType,
            fileName: file.fileName,
          }))
          .filter((file) => FileS.filterExtension(file, allowedExtensions));
        setFiles(entities);
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!entityId) {
    return null;
  }

  return (
    <>
      {loading && <SkeletonItems />}
      {!loading && files.length === 0 && <EmptyText mt="xs" />}
      {!loading && files.length > 0 && (
        <List
          mt="sm"
          spacing={0}
          size="xs"
          center
          icon={
            <ThemeIcon color="blue" size={24} radius="xl">
              <IconFileDescription size={16} />
            </ThemeIcon>
          }
        >
          {files.map((file) => (
            // eslint-disable-next-line react/jsx-key
            <List.Item p={5} className="hover-class" sx={{ cursor: 'pointer' }} onClick={() => _onSelect(file)}>
              {file.fileName}
            </List.Item>
          ))}
        </List>
      )}
      <FilePicker
        id={entityId}
        accept={accept}
        onSelectFile={(file) => {
          FileS.put(file, entityType, entityId).then((uploaded) => {
            if (uploaded) {
              _onSelect({ fileName: file.name, entityId, entityType });
            }
          });
        }}
        label={
          <Text mt="md" color="blue" variant="link" sx={{ cursor: 'pointer' }}>
            + Datei hochladen
          </Text>
        }
      />
    </>
  );
};

export default FilePickerModal;
